import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Check, Copy, Eye, EyeOff, Users } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { FormField } from "@/components/auth/FormField";
import {
  useCreateParentInvite,
  useMyParentInvites,
  useMyParents,
  useRevokeLinkedParent,
  useRevokeParentInvite,
} from "@/hooks/useParentInvites";
import { formatDate } from "@/lib/format";
import { env } from "@/lib/env";
import type { LinkedParent, ParentInvite } from "@/lib/api-types";

const schema = z.object({
  parent_email: z.string().trim().email("Enter a valid email").optional().or(z.literal("")),
});
type FormValues = z.infer<typeof schema>;

function inviteUrl(code: string): string {
  return `${env.VITE_APP_ORIGIN.replace(/\/+$/, "")}/auth/signup?invite=${code}`;
}

async function copy(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Couldn't copy -- select and copy manually");
  }
}

/**
 * The transparency card (Phase 8) -- the product's trust contract, stated in
 * plain language and kept exhaustive + current. A student must be able to
 * read exactly what a linked parent can and cannot see. If a future phase
 * ever exposes more to parents, this list is the thing that must change with
 * it -- it is deliberately concrete ("your actual conversations"), not vague.
 */
const PARENTS_CAN_SEE = [
  "Your study time and daily streak",
  "Your quiz and test scores",
  "Which chapters and subjects you're working on",
  "Topics you ask the AI tutor about (the chapter and how many questions -- never the questions themselves)",
  "Goals you've agreed on together, and tests they assign you",
];
const PARENTS_CANNOT_SEE = [
  "Your actual AI tutor conversations -- what you type or what it replies",
  "Anything you write or read outside StaxED",
  "Your password or login details",
];

export function TransparencyCard() {
  return (
    <Card className="p-5 sm:p-6 mt-3 border-brand/20 bg-brand-soft/40">
      <p className="text-sm font-medium">What a linked parent can see</p>
      <p className="text-xs text-text-muted mt-1">
        You're always in control -- here's exactly what's shared, and what stays private.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-subtle mb-2">
            <Eye className="h-3.5 w-3.5" /> They see
          </p>
          <ul className="flex flex-col gap-1.5">
            {PARENTS_CAN_SEE.map((line) => (
              <li key={line} className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-subtle mb-2">
            <EyeOff className="h-3.5 w-3.5" /> They never see
          </p>
          <ul className="flex flex-col gap-1.5">
            {PARENTS_CANNOT_SEE.map((line) => (
              <li key={line} className="flex items-start gap-2 text-sm">
                <EyeOff className="h-4 w-4 text-text-subtle flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}

/**
 * Student-side card for the student-first invite/linking flow (Phase 0 of
 * the parent portal). A parent account only ever comes into existence
 * through one of these invites -- there is no parent-initiated linking.
 */
export function LinkedParentsCard() {
  const invites = useMyParentInvites();
  const parents = useMyParents();
  const createInvite = useCreateParentInvite();
  const revokeInvite = useRevokeParentInvite();
  const revokeParent = useRevokeLinkedParent();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { parent_email: "" } });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      const result = await createInvite.mutateAsync({ parent_email: values.parent_email || undefined });
      reset({ parent_email: "" });
      if (result) {
        toast.success(
          values.parent_email ? "Invite sent -- also copied a share link below." : "Share link ready below.",
        );
        await copy(result.invite_url, "Invite link");
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { status_message?: string } } }).response?.data?.status_message ||
        (err as { message?: string }).message ||
        "Couldn't create an invite.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  });

  const onRevokeInvite = (invite: ParentInvite) => {
    const ok = window.confirm("Revoke this invite? The link will stop working.");
    if (!ok) return;
    revokeInvite.mutate(invite.invite_code, {
      onSuccess: () => toast.success("Invite revoked"),
      onError: () => toast.error("Couldn't revoke that invite"),
    });
  };

  const onRevokeParent = (parent: LinkedParent) => {
    const ok = window.confirm(
      `Remove ${parent.email || "this parent"}? They'll stop seeing your progress.`,
    );
    if (!ok) return;
    revokeParent.mutate(parent.parent_user_id, {
      onSuccess: () => toast.success("Parent link removed"),
      onError: () => toast.error("Couldn't remove that link"),
    });
  };

  return (
    <section className="mt-8">
      <h2 className="text-sm font-medium text-text-muted mb-3">Linked parents</h2>
      <Card className="p-5 sm:p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft">
            <Users className="h-5 w-5 text-brand" />
          </div>
          <p className="text-sm text-text-muted">
            Invite a parent to see your study time, scores and topics you're working on -- they never see
            your AI tutor conversations.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end" noValidate>
          <FormField
            id="parent_email"
            label="Parent's email (optional)"
            error={errors.parent_email?.message}
            className="flex-1"
          >
            <Input
              id="parent_email"
              type="email"
              placeholder="parent@example.com"
              aria-invalid={!!errors.parent_email}
              {...register("parent_email")}
            />
          </FormField>
          <Button type="submit" loading={submitting}>
            Create invite
          </Button>
        </form>

        {/* Pending invites -------------------------------------------------- */}
        <div className="mt-6">
          <p className="text-xs font-medium uppercase tracking-wide text-text-subtle mb-2">
            Pending invites
          </p>
          {invites.isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : (invites.data ?? []).length === 0 ? (
            <p className="text-sm text-text-muted">No pending invites.</p>
          ) : (
            <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
              {(invites.data ?? []).map((invite) => (
                <div
                  key={invite.invite_code}
                  className="flex flex-wrap items-center justify-between gap-2 p-3"
                >
                  <div className="min-w-0">
                    <p className="font-mono text-sm tracking-widest">{invite.invite_code}</p>
                    <p className="text-xs text-text-muted">
                      {invite.parent_email || "Share link"} - expires {formatDate(invite.expires_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => copy(inviteUrl(invite.invite_code), "Invite link")}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy link
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-danger hover:text-danger"
                      onClick={() => onRevokeInvite(invite)}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Linked parents ---------------------------------------------------- */}
        <div className="mt-6">
          <p className="text-xs font-medium uppercase tracking-wide text-text-subtle mb-2">
            Linked parents
          </p>
          {parents.isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : (parents.data ?? []).length === 0 ? (
            <p className="text-sm text-text-muted">No parents linked yet.</p>
          ) : (
            <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
              {(parents.data ?? []).map((parent) => (
                <div
                  key={parent.parent_user_id}
                  className="flex flex-wrap items-center justify-between gap-2 p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{parent.email || "Parent"}</p>
                    <p className="text-xs text-text-muted capitalize">
                      {parent.relationship} - linked {formatDate(parent.linked_at)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-danger hover:text-danger flex-shrink-0"
                    onClick={() => onRevokeParent(parent)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <TransparencyCard />
    </section>
  );
}
